import { QueryObserverOptions, useQuery } from "react-query";
import { APIError, APIResponse, API_URL_V1, API_URL_V3, get } from "../api";
import { useAuth } from "../context/AuthContext";

export interface NormalizedTransaction {
  id: string;
  description: string;
  amount: number;
  date: string;
  status: string;
  type: string;
}

/*
 * LEGACY Alert live source kept behind USE_LEGACY_ALERT_SOURCE for fast rollback.
 * NOTE this internal-source swap DROPS inflow visibility. Deposits and transfers-in are Alert-only.
 * DO NOT SHIP TO PRODUCTION until inflows are internalized (Path A) or product accepts the blind spot.
 */
const USE_LEGACY_ALERT_SOURCE = false;

/**
 * Normalize a single transaction from Alert API.
 * Alert may send Amount in kobo (divide by 100 for Naira) and negative for debits.
 * We store amount as signed; the UI displays absolute value with Credit/Debit badge (Option A).
 */
function normalizeAlertTransaction(raw: Record<string, unknown>, idx: number): NormalizedTransaction {
  const amountRaw = Number(raw.Amount ?? raw.amount ?? 0);
  const isKobo = raw.Amount !== undefined && raw.Amount !== null;
  const amount = isKobo ? amountRaw / 100 : amountRaw;

  return {
    id: String(raw.Id ?? raw.id ?? raw.ReferenceID ?? raw.reference ?? `tx-${idx}`),
    description: String(raw.Narration ?? raw.narration ?? raw.description ?? "Transaction"),
    amount,
    date: String(raw.TransactionDate ?? raw.transactionDate ?? raw.created_at ?? ""),
    status: String(raw.RecordType ?? raw.status ?? "successful").toLowerCase(),
    type: String(raw.RecordType ?? raw.type ?? "wallet_transfer"),
  };
}

function normalizeTransactions(raw: unknown[]): NormalizedTransaction[] {
  return raw.map((tx, idx) => normalizeAlertTransaction(tx as Record<string, unknown>, idx));
}

function extractTransactionsFromResponse(data: unknown): NormalizedTransaction[] {
  return normalizeTransactions(
    Array.isArray((data as any)?.Message)
      ? (data as { Message: unknown[] }).Message
      : Array.isArray((data as any)?.data)
      ? (data as { data: unknown[] }).data
      : Array.isArray(data)
      ? data
      : []
  );
}

/*
 * Amount-unit normalization for the internal `transactions` feed.
 *
 * The backend stores `transactions.amount` inconsistently: most types (bills,
 * transfers, cards, funding) are in Naira, but the credit-financing family stores
 * it in KOBO via NairaToKobo() at write time. Reading the raw value would render
 * those 100x too large. We divide ONLY the confirmed kobo types by 100 — a
 * conservative allowlist, so an unclassified type is never wrongly shrunk (worst
 * case it keeps the pre-existing 100x display, never under-reports money).
 *
 * Confirmed kobo types (Transaction::create + NairaToKobo, backend `master`):
 *   loan_commission, loan_repayment_prefund, down_payment.
 * Proper long-term fix is a single backend normalization (also for the admin
 * Transactions tab); tracked as a follow-up.
 */
const KOBO_TRANSACTION_TYPES = new Set([
  "loan_commission",
  "loan_repayment_prefund",
  "down_payment",
]);

function internalToNormalized(
  raw: Record<string, unknown>,
  idx: number
): NormalizedTransaction {
  const type = String(raw.type ?? "wallet_transfer");
  const rawAmount = Number(raw.amount ?? 0);
  const amount = KOBO_TRANSACTION_TYPES.has(type) ? rawAmount / 100 : rawAmount;

  return {
    id: String(raw.id ?? raw.public_id ?? raw.reference ?? `tx-${idx}`),
    description: String(raw.description ?? raw.note ?? raw.type ?? "Transaction"),
    amount,
    date: String(raw.created_at ?? ""),
    status: String(raw.status ?? "successful").toLowerCase(),
    type,
  };
}

function useAccountTransactions(
  options?: QueryObserverOptions<APIResponse<NormalizedTransaction[]>, APIError>
) {
  const { user } = useAuth();

  const {
    isLoading: isLoadingTransactions,
    isError: isTransactionsError,
    data: response,
    refetch: refreshTransactions,
    isRefetching,
  } = useQuery<APIResponse<NormalizedTransaction[]>, APIError>(
    "accountTransactions",
    async () => {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      if (USE_LEGACY_ALERT_SOURCE) {
        const result = await get(`${API_URL_V1}/accounts/transactions`, {
          headers,
        });
        const raw = result?.data ?? result;
        return { ...result, data: extractTransactionsFromResponse(raw) };
      }

      const result = await get(`${API_URL_V3}/account/users/transactions`, {
        headers: {
          ...headers,
        },
      });
      const rows = Array.isArray(result?.data?.data)
        ? result.data.data
        : Array.isArray(result?.data)
        ? result.data
        : [];

      return {
        ...result,
        data: rows.map((row: unknown, idx: number) =>
          internalToNormalized(row as Record<string, unknown>, idx)
        ),
      };
    },
    {
      enabled: user !== null,
      ...options,
    }
  );

  const transactions = response?.data ?? [];

  return {
    isLoadingTransactions,
    isTransactionsError,
    transactions,
    refreshTransactions,
    isRefetching,
  };
}

export default useAccountTransactions;
